import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { requestPanelNavigationAutoCollapse } from 'src/components/panelNavigationEvents'
import { selectDictionaries, selectExchangeCounters } from 'src/ducks/selectors'
import RoundTripOffersList from 'src/features/reservations/components/import/RoundTripOffersList'
import {
  selectExportActiveTab,
  selectExportFiltersFormatted,
  selectExchangeMapReservationRevision,
  selectExportMapLiveOffers,
  selectExportMapRemovedOfferIds,
  selectFreeCarrierFiltersFormatted,
  selectFreeCarriersActiveTab,
  selectFreeCarriersMapLiveOffers,
  selectFreeCarriersMapRemovedOfferIds,
  selectGeneralActiveTab,
  selectGeneralFiltersFormatted,
  selectGeneralMapLiveOffers,
  selectGeneralMapRemovedOfferIds,
  selectImportActiveTab,
  selectImportFiltersFormatted,
  selectImportMapLiveOffers,
  selectImportMapRemovedOfferIds,
  selectPostImportActiveTab,
  selectPostImportFiltersFormatted,
  selectPostImportMapLiveOffers,
  selectPostImportMapRemovedOfferIds
} from 'src/features/stocks/ducks/selectors'
import ExchangeOffersMap from './ExchangeOffersMap'
import ExchangeOffersViewModeContext from './ExchangeOffersViewModeContext'
import { OfferAgeFilterProvider } from '../filters/OfferAgeFilterContext'
import {
  EXCHANGE_MAP_TYPES,
  ROUND_TRIP_LIVE_HIGHLIGHT_MS,
  findRoundTripOffers,
  getRoundTripCandidateTypes,
  isRoundTripSuggestionEnabled,
  normalizeMapOffer,
  normalizeRoundTripMapOffers,
  reconcileRoundTripLiveOffers
} from './offerMapUtils'
import {
  exchangeOffersMapKeys,
  getMapGroupKey,
  invalidateExchangeMapQueries,
  useExchangeMapOffersQuery,
  useExchangeMapPointsQuery,
  useExchangeRoundTripQuery
} from './useExchangeOffersMapQueries'
import { scheduleMapQueryRefresh } from './scheduleMapQueryRefresh'

const DEFAULT_ROUND_TRIP_DISTANCE_KM = 50
const NOOP = () => {}
const EMPTY_ROUND_TRIP_DATA = { offers: [], mapLayers: [] }
const EMPTY_LIVE_OFFER_TIMES = {}

const MAP_FILTER_SELECTORS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: selectExportFiltersFormatted,
  [EXCHANGE_MAP_TYPES.IMPORT]: selectImportFiltersFormatted,
  [EXCHANGE_MAP_TYPES.GENERAL]: selectGeneralFiltersFormatted,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: selectPostImportFiltersFormatted,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: selectFreeCarrierFiltersFormatted
}

const MAP_ACTIVE_TAB_SELECTORS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: selectExportActiveTab,
  [EXCHANGE_MAP_TYPES.IMPORT]: selectImportActiveTab,
  [EXCHANGE_MAP_TYPES.GENERAL]: selectGeneralActiveTab,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: selectPostImportActiveTab,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: selectFreeCarriersActiveTab
}

const MAP_LIVE_OFFERS_SELECTORS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: selectExportMapLiveOffers,
  [EXCHANGE_MAP_TYPES.IMPORT]: selectImportMapLiveOffers,
  [EXCHANGE_MAP_TYPES.GENERAL]: selectGeneralMapLiveOffers,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: selectPostImportMapLiveOffers,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: selectFreeCarriersMapLiveOffers
}

const MAP_REMOVED_OFFER_IDS_SELECTORS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: selectExportMapRemovedOfferIds,
  [EXCHANGE_MAP_TYPES.IMPORT]: selectImportMapRemovedOfferIds,
  [EXCHANGE_MAP_TYPES.GENERAL]: selectGeneralMapRemovedOfferIds,
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: selectPostImportMapRemovedOfferIds,
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: selectFreeCarriersMapRemovedOfferIds
}

const emptyFilterSelector = () => ({})
const emptyActiveTabSelector = () => null
const emptyOffersSelector = () => []

const getMapQueryFilters = formattedFilters =>
  Object.keys(formattedFilters).reduce(
    (filters, key) =>
      key === 'tab_name' || key.startsWith('filter[')
        ? { ...filters, [key]: formattedFilters[key] }
        : filters,
    {}
  )

const getNestedMapFilters = formattedFilters =>
  Object.keys(formattedFilters).reduce((filters, key) => {
    const match = key.match(/^filter\[([^\]]+)\]$/)
    return match ? { ...filters, [match[1]]: formattedFilters[key] } : filters
  }, {})

const getInitialRoundTripFilters = (candidateTypes, mapOffer) => ({
  exchangeTypes: candidateTypes,
  maxDistanceKm: DEFAULT_ROUND_TRIP_DISTANCE_KM,
  daysAhead: 0,
  shipowner: mapOffer && mapOffer.shipownerId ? [mapOffer.shipownerId] : [],
  container: mapOffer && mapOffer.containerId ? [mapOffer.containerId] : []
})

const ListIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true'>
    <path
      fill='currentColor'
      d='M2 3h2v2H2V3zm3 0h9v2H5V3zM2 7h2v2H2V7zm3 0h9v2H5V7zm-3 4h2v2H2v-2zm3 0h9v2H5v-2z'
    />
  </svg>
)

const MapIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true'>
    <path
      fill='currentColor'
      d='M10.8 1.5 5.2 3.4 1 1.8v11.4l4.2 1.6 5.6-1.9 4.2 1.6V3.1l-4.2-1.6zm-.8 10.1-4 1.3V4.7l4-1.3v8.2zm-7.5-7.7 2 .8v8.2l-2-.8V3.9zm11 8.5-2-.8V3.4l2 .8v8.2z'
    />
  </svg>
)

const RoundTripViewOfferCount = ({ matches }) => {
  const intl = useIntl()
  return (
    <RoundTripViewCount>
      <strong>{matches.length}</strong>{' '}
      {intl.formatMessage(
        {
          id: 'reservations.roundTripOffersNoun',
          defaultMessage: '{count, plural, one {return offer} other {return offers}}'
        },
        { count: matches.length }
      )}
    </RoundTripViewCount>
  )
}

const ExchangeOffersView = ({
  autoSelectBestRoundTrip = false,
  children,
  className,
  compactMap = false,
  drawerOfferType,
  exchangeType,
  filters,
  initialRoundTripOffer,
  isDrawerOpen,
  onDrawerOfferTypeChange = NOOP,
  onRoundTripOfferSelect = NOOP,
  roundTripOnly = false,
  roundTripSourceLoading = false,
  roundTripSourceError = false,
  selectedOfferId,
  selectedOfferType,
  setDrawerState,
  showOffer
}) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [view, setView] = useState(
    roundTripOnly || !initialRoundTripOffer ? 'list' : 'map'
  )
  const [activeMapGroup, setActiveMapGroup] = useState(null)
  const [activeMapPage, setActiveMapPage] = useState(1)
  const roundTripCandidateTypes = useMemo(
    () =>
      getRoundTripCandidateTypes(exchangeType).filter(
        candidateExchangeType =>
          candidateExchangeType !== EXCHANGE_MAP_TYPES.GENERAL ||
          process.env.GATSBY_HIDE_GENERAL_CARGO !== 'true'
      ),
    [exchangeType]
  )
  const initialRoundTripMapOffer = useMemo(
    () =>
      initialRoundTripOffer
        ? normalizeMapOffer(initialRoundTripOffer, exchangeType)
        : null,
    [exchangeType, initialRoundTripOffer]
  )
  const initialRoundTripFilters = useMemo(
    () =>
      getInitialRoundTripFilters(
        roundTripCandidateTypes,
        initialRoundTripMapOffer
      ),
    [initialRoundTripMapOffer, roundTripCandidateTypes]
  )
  const [roundTripFilters, setRoundTripFilters] = useState(
    initialRoundTripFilters
  )
  const [roundTripRequest, setRoundTripRequest] = useState(() =>
    initialRoundTripMapOffer
      ? {
          selectedMapOffer: initialRoundTripMapOffer,
          filters: initialRoundTripFilters
        }
      : null
  )
  const mapFiltersSelector =
    MAP_FILTER_SELECTORS[exchangeType] || emptyFilterSelector
  const activeTabSelector =
    MAP_ACTIVE_TAB_SELECTORS[exchangeType] || emptyActiveTabSelector
  const liveOffersSelector =
    MAP_LIVE_OFFERS_SELECTORS[exchangeType] || emptyOffersSelector
  const removedOfferIdsSelector =
    MAP_REMOVED_OFFER_IDS_SELECTORS[exchangeType] || emptyOffersSelector
  const serializedMapFilters = useSelector(state =>
    JSON.stringify(getMapQueryFilters(mapFiltersSelector(state)))
  )
  const activeTab = useSelector(activeTabSelector)
  const liveOffers = useSelector(liveOffersSelector)
  const removedOfferIds = useSelector(removedOfferIdsSelector)
  const reservationRevision = useSelector(selectExchangeMapReservationRevision)
  const dictionaries = useSelector(selectDictionaries)
  const roundTripCandidateCounters = useSelector(state =>
    roundTripCandidateTypes
      .map(type => selectExchangeCounters(state)[type])
      .join(':')
  )
  const formattedMapFilters = useMemo(() => JSON.parse(serializedMapFilters), [
    serializedMapFilters
  ])
  const nestedMapFilters = useMemo(
    () => getNestedMapFilters(formattedMapFilters),
    [formattedMapFilters]
  )
  const liveMapRevision = useMemo(
    () =>
      JSON.stringify({
        live: liveOffers.map(offer => [offer.id, offer.liveNewOfferAddedAt]),
        removed: removedOfferIds
      }),
    [liveOffers, removedOfferIds]
  )
  const mapScopeKey = `${exchangeType}:${activeTab}:${serializedMapFilters}`
  const liveRefreshStateRef = useRef({
    mapScopeKey,
    liveMapRevision
  })
  const roundTripCandidateCountersRef = useRef(roundTripCandidateCounters)
  const reservationRevisionRef = useRef(reservationRevision)
  const mapPointsQuery = useExchangeMapPointsQuery({
    exchangeType,
    activeTab,
    filters: formattedMapFilters,
    enabled: view === 'map' && !roundTripOnly
  })
  const mapOffersQuery = useExchangeMapOffersQuery({
    exchangeType,
    activeTab,
    group: activeMapGroup,
    filters: nestedMapFilters,
    page: activeMapPage,
    enabled: view === 'map' && !roundTripOnly
  })
  const roundTripRequestReady =
    !roundTripOnly ||
    (roundTripRequest &&
      (roundTripRequest.selectedMapOffer === initialRoundTripMapOffer ||
        JSON.stringify(
          exchangeOffersMapKeys.roundTrip(
            exchangeType,
            roundTripRequest.selectedMapOffer,
            roundTripRequest.filters
          )
        ) ===
          JSON.stringify(
            exchangeOffersMapKeys.roundTrip(
              exchangeType,
              initialRoundTripMapOffer,
              roundTripRequest.filters
            )
          )))
  const roundTripQuery = useExchangeRoundTripQuery({
    exchangeType,
    selectedMapOffer: roundTripRequest && roundTripRequest.selectedMapOffer,
    filters: (roundTripRequest && roundTripRequest.filters) || roundTripFilters,
    liveRefresh: roundTripOnly,
    enabled:
      (view === 'map' || roundTripOnly) &&
      Boolean(roundTripRequest) &&
      !roundTripSourceLoading &&
      !roundTripSourceError &&
      roundTripRequestReady
  })
  const roundTripSearchKey = roundTripRequest
    ? JSON.stringify(
        exchangeOffersMapKeys.roundTrip(
          exchangeType,
          roundTripRequest.selectedMapOffer,
          roundTripRequest.filters
        )
      )
    : null
  const roundTripSeenRef = useRef({ queryKey: null, snapshot: null })
  const [roundTripLiveState, setRoundTripLiveState] = useState({
    queryKey: null,
    times: {}
  })
  const activeMapGroupKey = getMapGroupKey(activeMapGroup)
  const mapOffersData =
    mapOffersQuery.data && mapOffersQuery.data.groupKey === activeMapGroupKey
      ? mapOffersQuery.data
      : null
  const mapLayers = roundTripOnly
    ? []
    : (mapPointsQuery.data && mapPointsQuery.data.layers) || []
  const mapOffersCount = roundTripOnly
    ? 0
    : (mapPointsQuery.data && mapPointsQuery.data.offersCount) || 0
  const mapOffers = roundTripOnly
    ? []
    : (mapOffersData && mapOffersData.offers) || []
  const mapPagination = roundTripOnly
    ? {}
    : (mapOffersData && mapOffersData.pagination) || {}
  const roundTripData = roundTripQuery.data || EMPTY_ROUND_TRIP_DATA
  const roundTripInitialLoading =
    roundTripSourceLoading ||
    !roundTripRequestReady ||
    (roundTripQuery.isFetching && !roundTripQuery.data)
  const roundTripLoadError =
    roundTripSourceError || (roundTripQuery.isError && !roundTripQuery.data)
  const roundTripMapOffers = useMemo(
    () => normalizeRoundTripMapOffers(roundTripData.offers),
    [roundTripData.offers]
  )
  const roundTripSource = roundTripOnly
    ? initialRoundTripMapOffer
    : roundTripRequest && roundTripRequest.selectedMapOffer
  const roundTripMatches = useMemo(
    () =>
      findRoundTripOffers(
        roundTripSource,
        roundTripMapOffers,
        roundTripFilters
      ),
    [roundTripSource, roundTripMapOffers, roundTripFilters]
  )
  const roundTripLiveOfferTimes =
    roundTripLiveState.queryKey === roundTripSearchKey
      ? roundTripLiveState.times
      : EMPTY_LIVE_OFFER_TIMES

  useEffect(() => {
    if (reservationRevisionRef.current === reservationRevision) return

    reservationRevisionRef.current = reservationRevision
    scheduleMapQueryRefresh(queryClient, {
      queryKey: exchangeOffersMapKeys.all,
      predicate: query => query.queryKey[1] !== 'route'
    })
  }, [queryClient, reservationRevision])

  useEffect(() => {
    if (!roundTripOnly) return

    const previousSnapshot = roundTripSeenRef.current
    const queryChanged = previousSnapshot.queryKey !== roundTripSearchKey
    if (queryChanged) {
      roundTripSeenRef.current = {
        queryKey: roundTripSearchKey,
        snapshot: null
      }
    }
    if (!roundTripRequestReady || !roundTripQuery.data) {
      if (queryChanged) {
        setRoundTripLiveState({ queryKey: roundTripSearchKey, times: {} })
      }
      return
    }

    const snapshot = queryChanged ? null : previousSnapshot.snapshot
    const offers = roundTripQuery.data.offers
    const nextSnapshot = reconcileRoundTripLiveOffers(
      snapshot,
      offers,
      snapshot ? snapshot.liveOfferTimes : {},
      Date.now(),
      roundTripQuery.data.successfulExchangeTypes
    )
    roundTripSeenRef.current.snapshot = nextSnapshot
    setRoundTripLiveState({
      queryKey: roundTripSearchKey,
      times: nextSnapshot.liveOfferTimes
    })
  }, [
    roundTripOnly,
    roundTripQuery.data,
    roundTripRequestReady,
    roundTripSearchKey
  ])

  useEffect(() => {
    const markedTimes = Object.values(roundTripLiveState.times)
    if (markedTimes.length === 0) return undefined

    const nextExpiry = Math.min(...markedTimes) + ROUND_TRIP_LIVE_HIGHLIGHT_MS
    const timeout = window.setTimeout(() => {
      setRoundTripLiveState(previousState => {
        const times = {}
        Object.keys(previousState.times).forEach(key => {
          const markedAt = previousState.times[key]
          if (Date.now() - markedAt < ROUND_TRIP_LIVE_HIGHLIGHT_MS) {
            times[key] = markedAt
          }
        })
        return { ...previousState, times }
      })
    }, Math.max(1, nextExpiry - Date.now()))

    return () => window.clearTimeout(timeout)
  }, [roundTripLiveState])

  useEffect(() => {
    const previousCounters = roundTripCandidateCountersRef.current
    roundTripCandidateCountersRef.current = roundTripCandidateCounters
    if (
      previousCounters === roundTripCandidateCounters ||
      !roundTripRequest ||
      roundTripSourceLoading ||
      !roundTripRequestReady
    ) {
      return
    }

    scheduleMapQueryRefresh(queryClient, {
      queryKey: exchangeOffersMapKeys.roundTrip(
        exchangeType,
        roundTripRequest.selectedMapOffer,
        roundTripRequest.filters
      ),
      exact: true
    })
  }, [
    exchangeType,
    queryClient,
    roundTripCandidateCounters,
    roundTripOnly,
    roundTripRequest,
    roundTripRequestReady,
    roundTripSourceLoading
  ])

  useEffect(() => {
    requestPanelNavigationAutoCollapse(view === 'map')

    return () => {
      requestPanelNavigationAutoCollapse(false)
    }
  }, [view])

  useEffect(() => {
    if (view !== 'map') return

    setActiveMapGroup(null)
    setActiveMapPage(1)
  }, [mapScopeKey, view])

  useEffect(() => {
    const previousState = liveRefreshStateRef.current
    liveRefreshStateRef.current = { mapScopeKey, liveMapRevision }

    if (
      previousState.mapScopeKey !== mapScopeKey ||
      previousState.liveMapRevision === liveMapRevision ||
      view !== 'map' ||
      roundTripOnly
    ) {
      return undefined
    }

    const refreshTimeout = window.setTimeout(() => {
      invalidateExchangeMapQueries(queryClient, exchangeType, activeTab)
    }, 250)

    return () => window.clearTimeout(refreshTimeout)
  }, [
    activeTab,
    exchangeType,
    liveMapRevision,
    mapScopeKey,
    queryClient,
    roundTripOnly,
    view
  ])

  useEffect(() => {
    setRoundTripFilters(initialRoundTripFilters)
    setRoundTripRequest(
      initialRoundTripMapOffer
        ? {
            selectedMapOffer: initialRoundTripMapOffer,
            filters: initialRoundTripFilters
          }
        : null
    )
  }, [
    exchangeType,
    initialRoundTripFilters,
    initialRoundTripMapOffer,
    serializedMapFilters
  ])

  const loadRoundTripOffers = useCallback(
    (selectedMapOffer, activeRoundTripFilters = roundTripFilters) => {
      if (!isRoundTripSuggestionEnabled(exchangeType) || !selectedMapOffer) {
        return
      }

      setRoundTripRequest({
        selectedMapOffer,
        filters: activeRoundTripFilters
      })
    },
    [exchangeType, roundTripFilters]
  )

  const handleOfferSelect = id => {
    onDrawerOfferTypeChange(exchangeType, id)
    setDrawerState(true)
    showOffer(id, { origin: 'drawer' })
  }

  const handleMapLocationSelect = useCallback(
    group => {
      onDrawerOfferTypeChange(exchangeType)
      setDrawerState(false)
      setActiveMapGroup(group)
      setActiveMapPage(1)
    },
    [exchangeType, onDrawerOfferTypeChange, setDrawerState]
  )

  const clearMapOffers = useCallback(() => {
    setActiveMapGroup(null)
    setActiveMapPage(1)
  }, [])

  const handleMapClusterSelect = useCallback(() => {
    onDrawerOfferTypeChange(exchangeType)
    setDrawerState(false)
    clearMapOffers()
  }, [clearMapOffers, exchangeType, onDrawerOfferTypeChange, setDrawerState])

  const handleMapSelectionClear = useCallback(() => {
    onDrawerOfferTypeChange(exchangeType)
    setDrawerState(false)
    clearMapOffers()
  }, [clearMapOffers, exchangeType, onDrawerOfferTypeChange, setDrawerState])

  const handleMapPageChange = useCallback(
    page => {
      if (activeMapGroup) setActiveMapPage(page)
    },
    [activeMapGroup]
  )

  const handleViewChange = nextView => {
    setView(nextView)
  }

  const viewSwitch = (
    <ViewSwitch
      role='group'
      aria-label={intl.formatMessage({ id: 'offers.viewMode' })}
    >
      <ViewButton
        type='button'
        active={view === 'list'}
        aria-label={intl.formatMessage({ id: 'offers.listView' })}
        aria-pressed={view === 'list'}
        title={intl.formatMessage({ id: 'offers.listView' })}
        onClick={() => handleViewChange('list')}
      >
        <ListIcon />
      </ViewButton>
      <ViewButton
        type='button'
        active={view === 'map'}
        aria-label={intl.formatMessage({ id: 'offers.mapView' })}
        aria-pressed={view === 'map'}
        title={intl.formatMessage({ id: 'offers.mapView' })}
        onClick={() => handleViewChange('map')}
      >
        <MapIcon />
      </ViewButton>
    </ViewSwitch>
  )

  const exchangeListContents = React.isValidElement(children)
    ? React.cloneElement(children, {
        activeDrawerOfferId:
          drawerOfferType === exchangeType ? selectedOfferId : null,
        onDrawerOfferSelect: id => onDrawerOfferTypeChange(exchangeType, id)
      })
    : children

  const listContents = roundTripOnly ? (
    <RoundTripOffersList
      sourceOffer={initialRoundTripMapOffer}
      matches={roundTripMatches}
      liveOfferTimes={roundTripLiveOfferTimes}
      filters={roundTripFilters}
      candidateTypes={roundTripCandidateTypes}
      shipownerOptions={dictionaries.shipowners}
      containerOptions={dictionaries.containers}
      loading={roundTripInitialLoading}
      error={roundTripLoadError}
      failedExchangeTypes={roundTripData.failedExchangeTypes}
      isDrawerOpen={isDrawerOpen}
      selectedOfferId={selectedOfferId}
      selectedOfferType={selectedOfferType}
      onFiltersChange={setRoundTripFilters}
      onOffersLoad={loadRoundTripOffers}
      onOfferSelect={onRoundTripOfferSelect}
    />
  ) : (
    exchangeListContents
  )

  return (
    <OfferAgeFilterProvider
      scopeKey={`${exchangeType}:${activeTab}:${(initialRoundTripOffer &&
        initialRoundTripOffer.id) ||
        ''}`}
    >
      <ExchangeOffersViewModeContext.Provider value={viewSwitch}>
        <div className={className}>
          {roundTripOnly && (
            <RoundTripViewHeader>
              <RoundTripViewOfferCount matches={roundTripMatches} />
              {viewSwitch}
            </RoundTripViewHeader>
          )}
          {filters}
          {view === 'list' ? (
            listContents
          ) : (
            <ExchangeOffersMap
              activeDrawerOfferId={selectedOfferId}
              autoSelectBestRoundTrip={autoSelectBestRoundTrip}
              compact={compactMap}
              exchangeType={exchangeType}
              initialRoundTripOffer={initialRoundTripOffer}
              isDrawerOpen={isDrawerOpen}
              loading={!roundTripOnly && mapPointsQuery.isLoading}
              mapLayers={mapLayers}
              mapOffersCount={mapOffersCount}
              offers={mapOffers}
              offersError={!roundTripOnly && mapPointsQuery.isError}
              offersListError={!roundTripOnly && mapOffersQuery.isError}
              offersLoading={mapOffersQuery.isFetching}
              pagination={mapPagination}
              roundTripMapOffers={roundTripMapOffers}
              roundTripMatches={roundTripMatches}
              roundTripOffersError={roundTripLoadError}
              roundTripFailedExchangeTypes={roundTripData.failedExchangeTypes}
              roundTripCandidateTypes={roundTripCandidateTypes}
              roundTripContainerOptions={dictionaries.containers}
              roundTripFilters={roundTripFilters}
              roundTripMapLayers={roundTripData.mapLayers}
              roundTripLiveOfferTimes={roundTripLiveOfferTimes}
              roundTripOffersLoading={roundTripInitialLoading}
              roundTripShipownerOptions={dictionaries.shipowners}
              onClusterSelect={handleMapClusterSelect}
              onLocationSelect={handleMapLocationSelect}
              onPageChange={handleMapPageChange}
              onRoundTripOffersLoad={loadRoundTripOffers}
              onRoundTripFiltersChange={setRoundTripFilters}
              onRoundTripOfferSelect={onRoundTripOfferSelect}
              onSelectionClear={handleMapSelectionClear}
              roundTripOnly={roundTripOnly}
              selectedOfferId={
                selectedOfferType === exchangeType ? selectedOfferId : null
              }
              selectedOfferType={selectedOfferType}
              onOfferSelect={handleOfferSelect}
            />
          )}
        </div>
      </ExchangeOffersViewModeContext.Provider>
    </OfferAgeFilterProvider>
  )
}

const ViewSwitch = styled.span`
  display: inline-flex;
  padding: 1px;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.catskillWhite.hex()};
  box-shadow: 0 1px 3px rgba(36, 45, 63, 0.08);
`

const ViewButton = styled.button`
  display: inline-flex;
  width: 30px;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 3px;
  background: transparent;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 600;
  transition: background-color 0.15s, box-shadow 0.15s, color 0.15s;
  &:focus {
    position: relative;
    outline: 1px solid ${({ theme }) => theme.colors.easternBlue.hex()};
    outline-offset: 1px;
  }
  ${({ active, theme }) =>
    active &&
    css`
      background: ${theme.colors.white.hex()};
      box-shadow: 0 1px 5px rgba(36, 45, 63, 0.13);
      color: ${theme.colors.easternBlue.hex()};
    `}
`

const RoundTripViewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
  background: ${({ theme }) => theme.colors.white.hex()};
`

const RoundTripViewCount = styled.div`
  margin: 0;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.85rem;
  font-weight: 400;

  strong {
    font-weight: 700;
  }
`

export default styled(ExchangeOffersView)`
  display: flex;
  width: 100%;
  flex-direction: column;
`
